import { useState, useEffect, useCallback, useRef } from 'react';
import { X, Loader2, Smartphone } from 'lucide-react';
import { InputOTP, InputOTPGroup, InputOTPSlot } from './ui/input-otp';
import { Button } from './ui/button';
import { phoneAuthService } from '../../services/phoneAuthService';
import { captureError } from '../../lib/errorHandler';

interface PhoneOtpModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  phone: string;
  onVerified: (firebaseIdToken: string) => void;
  error?: string | null;
}

export function PhoneOtpModal({
  open,
  onOpenChange,
  phone,
  onVerified,
  error: externalError = null,
}: PhoneOtpModalProps) {
  const [code, setCode] = useState('');
  const [resendCountdown, setResendCountdown] = useState(60);
  const [isResending, setIsResending] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const verificationIdRef = useRef<string | null>(null);
  // Guards against double completion when manual entry and auto-verification race.
  const completedRef = useRef(false);
  // Ensures the auto-send-on-open effect fires exactly once per open.
  const hasSentRef = useRef(false);

  const fullPhone = '+91' + phone;

  // Mask phone: 98****3210
  const maskedPhone = (() => {
    if (phone.length < 4) return phone;
    return phone.slice(0, 2) + '****' + phone.slice(-4);
  })();

  // Auto-verified (Android instant/auto-retrieval) — get token, no code entry.
  const completeAutoVerified = useCallback(async () => {
    if (completedRef.current) return;
    completedRef.current = true;
    setIsVerifying(true);
    setError(null);
    try {
      const idToken = await phoneAuthService.getIdToken();
      await phoneAuthService.signOut();
      onVerified(idToken);
    } catch (err: any) {
      completedRef.current = false;
      captureError(err, 'PhoneOtpModal.completeAutoVerified', { phone: fullPhone });
      setError(err?.message || 'Verification failed. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  }, [fullPhone, onVerified]);

  const sendCode = useCallback(async () => {
    setIsSending(true);
    setError(null);
    try {
      const result = await phoneAuthService.sendVerificationCode(fullPhone, () => {
        void completeAutoVerified();
      });
      if (result.autoVerified) {
        await completeAutoVerified();
        return;
      }
      verificationIdRef.current = result.verificationId;
    } catch (err: any) {
      captureError(err, 'PhoneOtpModal.sendVerificationCode', { phone: fullPhone });
      setError(err?.message || 'Failed to send verification code. Please try again.');
    } finally {
      setIsSending(false);
    }
  }, [fullPhone, completeAutoVerified]);

  // Send code on open — exactly once per open. `sendCode` can change identity
  // when the parent re-renders (its deps include the parent-supplied callback);
  // the `hasSentRef` guard prevents that from re-triggering a fresh SMS, which
  // would invalidate the previous verification session.
  useEffect(() => {
    if (!open) {
      setCode('');
      setResendCountdown(60);
      setError(null);
      verificationIdRef.current = null;
      completedRef.current = false;
      hasSentRef.current = false;
      // Drop any pending auto-verification listener.
      void phoneAuthService.cleanup();
      return;
    }
    if (hasSentRef.current) return;
    hasSentRef.current = true;
    sendCode();
  }, [open, sendCode]);

  // Countdown timer
  useEffect(() => {
    if (!open) return;
    const timer = setInterval(() => {
      setResendCountdown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [open]);

  const handleComplete = useCallback(
    async (value: string) => {
      if (value.length !== 6 || !verificationIdRef.current) return;
      if (completedRef.current) return;
      completedRef.current = true;
      setIsVerifying(true);
      setError(null);
      try {
        const idToken = await phoneAuthService.confirmCode(verificationIdRef.current, value);
        // Sign out Firebase session — we only need the token
        await phoneAuthService.signOut();
        onVerified(idToken);
      } catch (err: any) {
        completedRef.current = false; // allow retry with a new code
        captureError(err, 'PhoneOtpModal.confirmCode', { phone: fullPhone });
        setError(err?.message || 'Invalid verification code. Please try again.');
        setCode('');
      } finally {
        setIsVerifying(false);
      }
    },
    [fullPhone, onVerified],
  );

  const handleResend = async () => {
    setIsResending(true);
    setError(null);
    try {
      const result = await phoneAuthService.sendVerificationCode(fullPhone, () => {
        void completeAutoVerified();
      });
      if (result.autoVerified) {
        await completeAutoVerified();
        return;
      }
      verificationIdRef.current = result.verificationId;
      setResendCountdown(60);
      setCode('');
    } catch (err: any) {
      captureError(err, 'PhoneOtpModal.resendVerificationCode', { phone: fullPhone });
      setError(err?.message || 'Failed to resend code.');
    } finally {
      setIsResending(false);
    }
  };

  const displayError = externalError || error;

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-[2000] flex items-end sm:items-center justify-center">
      <div className="bg-white dark:bg-gray-900 rounded-t-3xl sm:rounded-3xl w-full sm:max-w-md max-h-[90vh] overflow-auto pb-[env(safe-area-inset-bottom)]">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-6 py-4 rounded-t-3xl">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Verify Phone</h2>
            <button
              onClick={() => onOpenChange(false)}
              disabled={isVerifying || isSending}
              className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-700 transition-all disabled:opacity-50"
            >
              <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 flex flex-col items-center">
          {/* Phone Icon */}
          <div className="w-16 h-16 rounded-full bg-[#8bc43e]/10 flex items-center justify-center mb-4">
            <Smartphone className="w-8 h-8 text-[#8bc43e]" />
          </div>

          {isSending ? (
            <div className="flex flex-col items-center mb-6">
              <Loader2 className="w-6 h-6 animate-spin text-[#8bc43e] mb-2" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Sending verification code...</p>
            </div>
          ) : (
            <>
              <p className="text-center text-gray-600 dark:text-gray-300 mb-2">
                We sent a 6-digit code to
              </p>
              <p className="text-center font-semibold text-gray-900 dark:text-white mb-6">
                +91 {maskedPhone}
              </p>
            </>
          )}

          {/* OTP Input */}
          <div className="mb-6">
            <InputOTP
              maxLength={6}
              value={code}
              onChange={(value) => {
                setCode(value);
                if (value.length === 6) {
                  handleComplete(value);
                }
              }}
              disabled={isVerifying || isSending}
            >
              <InputOTPGroup className="gap-2">
                {[0, 1, 2, 3, 4, 5].map((index) => (
                  <InputOTPSlot
                    key={index}
                    index={index}
                    className="w-12 h-14 text-lg font-semibold border-2 rounded-xl"
                  />
                ))}
              </InputOTPGroup>
            </InputOTP>
          </div>

          {/* Error */}
          {displayError && (
            <div className="w-full bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-3 mb-4">
              <p className="text-sm text-red-600 dark:text-red-400 text-center">{displayError}</p>
            </div>
          )}

          {/* Verify Button */}
          <Button
            onClick={() => handleComplete(code)}
            disabled={code.length !== 6 || isVerifying || isSending}
            className="w-full h-14 bg-[#8bc43e] hover:bg-[#7ab32e] text-white rounded-xl font-semibold disabled:opacity-50 mb-4"
          >
            {isVerifying ? (
              <div className="flex items-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Verifying...</span>
              </div>
            ) : (
              'Verify'
            )}
          </Button>

          {/* Resend */}
          <div className="text-center">
            {resendCountdown > 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Resend code in <span className="font-semibold text-gray-900 dark:text-white">{resendCountdown}s</span>
              </p>
            ) : (
              <button
                onClick={handleResend}
                disabled={isResending}
                className="text-sm text-[#8bc43e] font-semibold hover:underline disabled:opacity-50"
              >
                {isResending ? 'Sending...' : 'Resend Code'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
